<template>
  <b-container style="width:600px;">
    <b-card>
      <h3>Activate 1.1 Course</h3>
      <br />
      <b-card-text>
        <b-input-group prepend="Identifier">
          <b-form-input v-model="form.context_id" @keyup.enter="checkCourse" type="text"></b-form-input>
          <b-input-group-append>
            <!-- TWA-215: disabled while a move is in flight. startReassign captures the
                 course and tab index at press time, but the modal only blocks input once
                 it is open — so during the dry-run round trip a new search could replace
                 `courses`, and the confirm would then swap the moved row into whatever
                 landed at that index. The move itself stays correct; the tab would not. -->
            <b-button variant="success" :disabled="isReassigning" @click="checkCourse">Submit
              <b-spinner v-if="isSending" class="m-1" label="Spinning" small />
            </b-button>
          </b-input-group-append>
        </b-input-group>
        <ul class="mt-3">
          <li v-for="error in course_errors" :key="error" style="color:red">{{ error }}</li>
        </ul>

        <div v-if="courses">
          <b-tabs>
            <b-tab v-for="(course, index) in courses" :key="course.id"
              :title="courseLabel(course)">
              <br>
              <p v-if="course.products.length && isTwaHosted(course)">
                Join Link: <span class="mr-2" style="user-select: all">{{ join_url(index) }}</span>
                <a :href="join_url(index)" target="_blank">
                  <b-icon font-scale="1.2" icon="box-arrow-up-right"></b-icon>
                </a>
              </p>
              CID: {{ course.canvas_course_id }}
              <a v-if="course.canvas_course_id && course.canvas_domain"
                 :href="`https://${course.canvas_domain}/courses/${course.canvas_course_id}`"
                 target="_blank"
                 rel="noopener noreferrer"
                 class="ml-2">
                <b-icon font-scale="1.2" icon="box-arrow-up-right"></b-icon>
              </a><br>
              Name: {{ course.context_title }}<br>
              Code: {{ course.context_label }}<br>
              Domain: {{ course.canvas_domain }}<br>
              <!-- TWA-247: 'deleted' is not a Canvas publish state a teacher can fix — the
                   Canvas course is gone and this row outlived it. Say so, in red, rather than
                   sitting it alongside 'available'/'unpublished' in the same plain styling. -->
              State:
              <span v-if="course.workflow_state === 'deleted'" class="text-danger font-weight-bold">
                Deleted on Canvas
              </span>
              <span v-else>{{ course.workflow_state }}</span><br>
              <div v-if="isTwaHosted(course)">
                <div v-if="course.term_info && (course.term_info.start_at || course.term_info.end_at)">
                  Term Date: {{ course.term_info.start_at || 'whenever' }} - {{ course.term_info.end_at || 'whenever' }}<br>
                </div><br>
                <div class="d-flex align-items-center my-2">
                  <span class="mr-2">Expires:</span>
                  <b-form-datepicker v-model="form.expires_at[index]" :min="today" class="w-50"></b-form-datepicker>
                  <b-button size="sm" variant="outline-secondary" class="ml-2" @click="clearExpiryDate(index)">
                    Clear
                  </b-button>
                </div>
              </div>

              <!-- WTS Toggle (TWA-256: tri-state, mirroring Teacher Resources below) -->
              <div class="d-flex align-items-center my-1">
                <span class="mr-2">WTS Access:</span>
                <b-form-select v-model="form.allows_wts[index]" size="sm" style="width: 200px;">
                  <option :value="null">Inherit from School ({{ course.lti_consumer.allows_wts ? 'Enabled' : 'Disabled' }})</option>
                  <option :value="true">Enabled</option>
                  <option :value="false">Disabled</option>
                </b-form-select>
              </div>

              <!-- Teacher Resources Toggle -->
              <div class="d-flex align-items-center my-1">
                <span class="mr-2">Teacher Resources:</span>
                <b-form-select v-model="form.shows_teacher_resources[index]" size="sm" style="width: 200px;">
                  <option :value="null">Inherit from School</option>
                  <option :value="true">Enabled</option>
                  <option :value="false">Disabled</option>
                </b-form-select>
              </div>
              <!-- Auto-Enroll Access Code (TWA-231: course-level binding) -->
              <div class="d-flex align-items-center my-1">
                <span class="mr-2">Auto-Enroll Access Code:</span>
                <b-form-input v-model.trim="form.auto_enroll_coupon_code[index]" size="sm"
                  style="width: 260px;" placeholder="Blank = inherit from school"></b-form-input>
              </div>
              <small v-if="course.auto_enroll_coupon_code" class="text-muted d-block">
                Auto-Enroll (Course): <strong>{{ course.auto_enroll_coupon_code }}</strong>
                ({{ course.auto_enroll_product_code }}) —
                {{ course.auto_enroll_usage }}/{{ course.auto_enroll_max_usage }} used
              </small>
              <b-alert v-if="autoEnrollMismatch(index)" show variant="warning" class="mt-1 py-1 px-2">
                <small>
                  {{ autoEnrollMismatchReason(index) }}
                  Students will be <strong>blocked</strong> — auto-enroll offers no
                  pay/access-code fallback.
                </small>
              </b-alert>
              <small class="text-muted d-block">
                Auto-Enroll (School):
                <span v-if="course.lti_consumer.auto_enroll_coupon_code">
                  {{ course.lti_consumer.auto_enroll_coupon_code }}
                  ({{ course.lti_consumer.auto_enroll_product_code }}) —
                  {{ course.lti_consumer.auto_enroll_usage }}/{{ course.lti_consumer.auto_enroll_max_usage }} used
                </span>
                <span v-else>None</span>
                · {{ course.lti_consumer.name }}
                (<router-link :to="`/manage_consumer?key=${course.lti_consumer.key}`">{{ course.lti_consumer.key }}</router-link>)
              </small>

              <!-- TWA-275: WTS + auto-enroll both effectively on. Not an error — manually
                   rostered premium KSS schools run this deliberately (TWA-283) — so this
                   states the consequence and lets the admin judge.

                   A plain div with v-if, not <b-alert>: b-alert kept a stale warning on
                   screen when the condition went false (verified in the browser — the node
                   kept its `show` class and old text through both a static `show` and a
                   bound `:show`). The alert-warning classes give the same styling. -->
              <div v-if="wtsAutoEnrollWarnings[index]" class="alert alert-warning mt-2 py-2 px-2" role="alert">
                <small>
                  <strong>ASL Dictionary is on and an auto-enroll code is set
                  ({{ wtsAutoEnrollWarnings[index].wtsFrom }} / {{ wtsAutoEnrollWarnings[index].codeFrom }}).</strong>
                  Students reach the dictionary without signing up, so they will not be
                  enrolled, no seats will be claimed, and no grade is passed back.
                  Fine if this school is manually rostered; not fine if students are
                  meant to complete the Sign Up module.
                </small>
              </div>
              <br>

              School: {{ course.lti_consumer.name }} (<router-link :to="`/manage_consumer?key=${course.lti_consumer.key}`">{{ course.lti_consumer.key }}</router-link>)<br>
              Accepts Payment: {{ course.lti_consumer.accepts_payment ? 'Enabled' : 'Disabled' }}<br>
              Accepts Access Code: {{ course.lti_consumer.accepts_access_code ? 'Enabled' : 'Disabled' }}<br>
              Shows Teacher Resources (School): {{ course.lti_consumer.shows_teacher_resources !== false ? 'Enabled' : 'Disabled' }}<br>

              <!-- Move to another school (TWA-215). The resolution path for the TWA-191
                   409: the launch is refused because this row sits under a different
                   consumer key than the one now signing launches, and this re-points it
                   instead of an engineer running SQL on prod. -->
              <div class="d-flex align-items-center my-2">
                <span class="mr-2">Move to School:</span>
                <b-form-input v-model.trim="form.reassign_key[index]" size="sm"
                  style="width: 220px;" placeholder="target consumer key"></b-form-input>
                <b-button size="sm" variant="outline-danger" class="ml-2"
                  :disabled="!form.reassign_key[index] || isReassigning || isSending"
                  @click="startReassign(index)">
                  Move
                  <b-spinner v-if="isReassigning" class="ml-1" label="Spinning" small />
                </b-button>
              </div>
              <!-- TWA-215: a move that landed but could not be shown, and TWA-829's raced
                   duplicate, both need to outlive a 2.5s toast — the tab still shows the OLD
                   school, so an admin who missed the toast will read a stale row as current
                   and can press Change against the wrong consumer. Stays until re-search. -->
              <div v-if="moveNotices[index]" class="alert alert-warning py-2 px-2 my-2" role="alert">
                <small>{{ moveNotices[index] }}</small>
              </div>
              <!-- Names the trigger in the words of the person doing it. The key usually
                   changes as a SIDE EFFECT of moving a course between Canvas sub-accounts
                   (each sub-account's tool install carries its own key), not because anyone
                   deliberately "switched keys" — so a support person who just reparented a
                   course would not recognise this control as their fix (TWA-352). -->
              <small class="text-muted d-block mb-2">
                Use this when a course was moved to a different Canvas sub-account and its
                launches now fail with "already registered under a different consumer key" —
                the new sub-account's tool signs with its own key.
              </small>
              <br>
              Teacher(s):
              <div v-for="teacher in course.teachers" :key="teacher.id">
                {{ teacher.display_name }}
              </div><br>

              Products:
              <span class="text-success font-weight-bold">{{ course.products.map(product =>
                product.product_code).join(', ') }}</span>
              <b-alert
                v-if="course.products.length === 0 && course.lti_consumer.product_id"
                show variant="info" class="mt-2 mb-2 py-2">
                <small>
                  <b-icon icon="info-circle"></b-icon>
                  On next launch, this course will be auto-activated with
                  <strong>{{ productLabel(course.lti_consumer.product_id) }}</strong>
                  from the consumer's auto-activate setting.
                </small>
              </b-alert>
              <!-- TWA-257: one product per course going forward. The 190 legacy courses that
                   carry more than one are 2021 rows with 3,351 enrollments recorded against
                   them; the product set is what records what those courses granted, so it is
                   evidence and must not be reduced by accident.

                   They are LOCKED rather than shown in a single-select, because a single-select
                   cannot represent a 4-product row — it would display one value while the row
                   holds four, and "Change" posts the whole tab (see setProducts), so editing
                   WTS access on a legacy course would silently drop the other three. Read-only
                   text always tells the truth about the row; the picker appears only once the
                   admin has declared the intent to reduce. -->
              <div v-if="isLegacyMultiProduct(index)">
                <b-alert show variant="warning" class="mt-2 mb-2 py-2">
                  <small>
                    <strong>Legacy multi-product course
                    ({{ course.products.length }} products).</strong>
                    Courses now take a single product. These rows are from a 2021 configuration
                    and are kept as a record of what the course granted, so the products are
                    read-only. Converting reduces this course to one product and cannot be undone
                    here.
                  </small>
                </b-alert>
                <b-button class="mt-1 mb-2" size="sm" variant="outline-danger"
                          @click="convertToSingleProduct(index)">
                  Convert to a single product…
                </b-button>
              </div>
              <b-form-select v-else class="mt-2 mb-2" v-model="form.selected_products[index]"
                :options="products_options"></b-form-select>
              <b-button v-if="course" class="mt-2" size="sm" text="Button" variant="primary" @click="setProducts(index)"
                :disabled="is_button_disabled(index)">
                Change
              </b-button>
              <br><br>
              <div v-if="activation_histories[index]">
                Activation History:
                <b-table :bordered="true" :small="true" :items="activation_histories[index]"></b-table>
              </div>
            </b-tab>
          </b-tabs>

        </div>

      </b-card-text>

    </b-card>

    <!-- Confirm course move (TWA-215). Everything shown here comes from the server's
         dry run: the move is billing-adjacent (it carries paid enrollments to another
         school) and it silently changes every setting the course inherits. -->
    <b-modal
      v-model="showReassignModal"
      title="Confirm move to another school"
      size="lg"
      @hidden="onReassignCancel">
      <div v-if="reassignImpact">
        <p class="my-2">
          Moving <strong>{{ reassignImpact.from.name }}</strong> ({{ reassignImpact.from.key }})
          → <strong>{{ reassignImpact.to.name }}</strong> ({{ reassignImpact.to.key }}).
        </p>
        <p class="my-2">
          <strong>{{ reassignImpact.enrollment_count }}</strong>
          enrollment{{ reassignImpact.enrollment_count === 1 ? '' : 's' }} move with it
          ({{ reassignImpact.processed_count }} processed,
          <strong>{{ reassignImpact.paid_count }}</strong> with a recorded payment).
          Payments, access codes and activation history follow the course.
        </p>
        <!-- A pending 1.1 enrollment keeps the passback URL and sourcedid the SOURCE
             install issued at launch, but passback signs with whatever key/secret the
             course's consumer has now (LtiEnrollment#passback) — so after a move it
             signs a source-issued sourcedid with the target's secret and the LMS
             rejects it. The student sees that as a failed enrollment. It self-heals:
             the next launch under the new key rewrites both fields. -->
        <b-alert v-if="reassignImpact.pending_passback_count > 0" show variant="warning" class="py-2">
          <small>
            <strong>{{ reassignImpact.pending_passback_count }}</strong> unprocessed LTI 1.1
            enrollment{{ reassignImpact.pending_passback_count === 1 ? '' : 's' }} will
            <strong>fail grade passback</strong> after this move — the stored passback target still
            belongs to {{ reassignImpact.from.key }}, but it will be signed with
            {{ reassignImpact.to.key }}'s secret. Each student recovers by launching the course
            again from their LMS under {{ reassignImpact.to.key }}. Process pending grades before
            moving if you can.
          </small>
        </b-alert>
        <div v-if="reassignChanges.length">
          <p class="mb-1"><strong>Settings that change:</strong></p>
          <ul class="mb-2">
            <li v-for="change in reassignChanges" :key="change.label">
              {{ change.label }}: <strong>{{ change.from }}</strong> → <strong>{{ change.to }}</strong>
              <small v-if="change.inherited" class="text-muted">(inherited from the school)</small>
            </li>
          </ul>
        </div>
        <p v-else class="my-2 text-muted">No effective settings change.</p>
      </div>
      <template #modal-footer>
        <b-button variant="secondary" @click="onReassignCancel">Cancel</b-button>
        <b-button variant="danger" @click="onReassignConfirm">
          Move course
        </b-button>
      </template>
    </b-modal>
  </b-container>
</template>

<script>
import { checkLtiCourses, getProducts, activateLtiCourse, reassignCourse, getUsers } from "@/utils/twa-api";
import { isTwaHosted, BASIC_DOMAINS } from "@/utils/platform";
import { getErrorMessage, getDetailedErrorMessage } from "@/utils/error-messages";
import notify from "@/mixins/notify";


export default {
  mixins: [notify],
  data() {
    return {
      form: {
        context_id: this.$route.params.context_id,
        selected_products: {},
        allows_wts: {},
        shows_teacher_resources: {},
        auto_enroll_coupon_code: {},
        accepts_payment: {},
        accepts_access_code: {},
        expires_at: {},
        term_start_date: {},
        term_end_date: {},
        reassign_key: {},
      },
      isSending: false,
      // TWA-257: indexes of legacy multi-product courses the admin has explicitly
      // chosen to reduce. Session-only and deliberately not persisted — reopening
      // the screen re-locks the course.
      convertedProducts: {},
      isReassigning: false,
      showReassignModal: false,
      reassignImpact: null,
      reassignIndex: null,
      reassignResolver: null,
      // TWA-215: per-tab notice that a move landed but the tab cannot show it, or that a
      // duplicate appeared during it (TWA-829). Index-keyed and cleared on every lookup,
      // for the same reason the reassign key is: a stale entry would otherwise attach to
      // whatever course lands at that index next.
      moveNotices: {},
      course_errors: [],
      courses: [],
      activations: [],
      activation_histories: {},
      today: new Date().toISOString().split('T')[0],
      allProducts: [],
      allUsers: [],
    }
  },
  computed: {
    products() {
      return this.allProducts.filter(p => p.name.toLowerCase() !== 'zfree').sort((a, b) => a.name.localeCompare(b.name))
    },
    products_options() {
      return this.products.map(function (u) {
        return { value: u.id, text: u.name + ' (' + u.product_code + ') ' }
      })
    },
    users() {
      return [...this.allUsers].sort((a, b) => a.id - b.id)
    },
    // TWA-275: per course tab, is WTS on AND an auto-enroll code set? Each entry is
    // {wtsFrom, codeFrom} naming where each comes from, or null.
    //
    // A computed keyed by index rather than a method taking one, so the tracked
    // dependency is the whole form array and flipping the dropdown re-renders.
    //
    // Reads the pending form values, not the saved row. Both settings are tri-state
    // (NULL = inherit) and the case that matters most is a course inheriting BOTH from
    // its school, where neither control reads "on" — the shape the 2026-08-10 Rio
    // Rancho batch had.
    wtsAutoEnrollWarnings() {
      return this.courses.map((course, index) => {
        if (!course) return null

        // allows_wts has no effect without products: verify_request bails out with
        // "not yet activated" before ever reaching the WTS branch. Warning on a course
        // that cannot have the problem is how people learn to dismiss the warning.
        // Unless the school has an auto-activate product: the next launch activates a
        // product-less course with it BEFORE that bail-out, so the state goes live anyway.
        // Pending selection, not course.products, so the note fires on the same save
        // that first activates the course. TWA-257 made selected_products a scalar id;
        // productIdsFor turns it back into the list the save will send, and is null for
        // a locked legacy course or no selection — then the products stay as they are.
        const pendingProducts = this.productIdsFor(index) || course.products.map(p => p.id)
        if (!pendingProducts.length && !course.lti_consumer.product_id) return null

        const formWts = this.form.allows_wts[index]
        const inheritsWts = formWts === null || formWts === undefined
        const wtsOn = inheritsWts ? !!course.lti_consumer.allows_wts : !!formWts
        if (!wtsOn) return null

        const formCode = (this.form.auto_enroll_coupon_code[index] || '').trim()
        if (!formCode && !course.lti_consumer.auto_enroll_coupon_code) return null

        // Only while the admin is creating this state (TWA-275 hand-off point 2): an
        // existing both-set row is usually deliberate after the premium-KSS sweep
        // (TWA-283), so it gets no badge on load. Products count as a change so the
        // inherit-both case still fires when the admin sets the first product — there
        // they touch neither switch. A product-less course that auto-activates from the
        // school gets the note only if this tab changes WTS or the code; inheriting both
        // with nothing changed here is created on the school screen, not this one.
        const changed =
          JSON.stringify(pendingProducts) !== JSON.stringify(course.products.map(p => p.id)) ||
          formWts !== course.allows_wts ||
          formCode !== (course.auto_enroll_coupon_code || '')
        if (!changed) return null

        return {
          wtsFrom: inheritsWts ? 'inherited from the school' : 'set on this course',
          codeFrom: formCode ? 'code on this course' : 'code from the school',
        }
      })
    },
    // The settings the move would actually change, diffed from the server's dry run.
    // This is the non-obvious half of a move: a course carries NULL for the tri-state
    // flags meaning "inherit", and accepts_payment / accepts_access_code / email_domain
    // live only on the school — so a move can switch off card payment or flip dictionary
    // access with no edit to the course itself.
    reassignChanges() {
      const impact = this.reassignImpact
      if (!impact) return []
      const course = this.courses[this.reassignIndex]
      const fields = [
        { key: 'allows_wts', label: 'ASL Dictionary', inheritedKey: 'allows_wts_inherited' },
        { key: 'shows_teacher_resources', label: 'Teacher Resources', inheritedKey: 'shows_teacher_resources_inherited' },
        { key: 'auto_enroll_coupon_code', label: 'Auto-Enroll Code', inheritedKey: 'auto_enroll_inherited' },
        { key: 'accepts_payment', label: 'Accepts Payment' },
        { key: 'accepts_access_code', label: 'Accepts Access Code' },
        { key: 'email_domain', label: 'Email Domain' },
        // The school's auto-activate product: a product-less course moved between
        // schools with different settings silently changes what it activates with
        // on next launch, which is invisible without this row. Only a product-less
        // course, though — every activate_from_consumer call site is guarded by
        // `if lti_course.products.empty?`, so for a course that already has a product
        // the school's setting can never fire and this row would be a false positive.
        ...(course && course.products.length === 0
          ? [{ key: 'product_id', label: 'Auto-Activate Product', format: 'product' }]
          : []),
      ]
      // Compare what the admin will actually SEE, not the raw values: a cleared
      // email_domain is "" on one consumer and NULL on another, and the nullable
      // boolean columns give NULL vs false — both render identically, so a raw !==
      // would list a change that reads as "None → None".
      return fields
        .map(f => ({
          label: f.label,
          from: this.settingLabel(impact.from[f.key], f.format),
          to: this.settingLabel(impact.to[f.key], f.format),
          inherited: f.inheritedKey ? impact.to[f.inheritedKey] : true,
        }))
        .filter(change => change.from !== change.to)
    },
  },
  methods: {
    isTwaHosted,  // Expose imported function to template
    productLabel(productId) {
      const p = this.allProducts.find(x => x.id === productId)
      return p ? `${p.name} (${p.product_code})` : `product #${productId}`
    },
    autoEnrollMismatch(index) {
      const course = this.courses[index]
      if (!course || !course.auto_enroll_coupon_code) return false
      // Auto-enroll only offers on single-product courses; anything else blocks.
      if (course.products.length !== 1) return true
      return course.products[0].product_code !== course.auto_enroll_product_code
    },
    // TWA-257: three different things block here and the banner has to name the right
    // one. "Does not match" is only true of a single product that disagrees with the
    // coupon's; several products is a shape auto-enroll does not support (the API
    // returns :multi_product), and no product at all is nothing to match against.
    autoEnrollMismatchReason(index) {
      const course = this.courses[index]
      if (!course) return ''
      if (course.products.length > 1) {
        return `This course has ${course.products.length} products; auto-enroll requires ` +
          'exactly one. Convert it to a single product, or clear the field to inherit from the school.'
      }
      if (course.products.length === 0) {
        return 'This course has no product, so auto-enroll has nothing to grant. ' +
          'Set a product, or clear the field to inherit from the school.'
      }
      return `This code's product (${course.auto_enroll_product_code}) does not match this ` +
        "course's product. Fix the code, or clear the field to inherit from the school."
    },
    courseLabel(course) {
      let label
      if (BASIC_DOMAINS.includes(course.canvas_domain)) label = 'Basic'
      else if (isTwaHosted(course)) label = 'Premium'
      else label = 'School-Hosted'
      // On a cross-consumer collision (TWA-191) both rows are the same Canvas course,
      // so the hosting label alone renders two identical tabs. The consumer key is the
      // only thing that tells them apart, and it's what the admin picks between when
      // deciding which row to move.
      if (this.courses.length > 1) return `${label} · ${course.lti_consumer.key}`
      return label
    },
    handleLoginEvent(data) {
      this.isAuthenticated = data.loggedIn
      this.profile = data.profile
    },
    async getData() {
      try {
        this.allProducts = await getProducts()
        this.allUsers = await getUsers()
      } catch (error) {
        this.notifyMe(getErrorMessage(error))
        if (error.response && error.response.status === 401) {
          this.$auth.logOut('Unauthorized.')
        }
      }
    },
    // TWA-257: a course is locked while it still carries >1 product and the admin has
    // not converted it in this session. Reads the SAVED row, not the form: the form's
    // selected_products is what a conversion is about to change it to.
    isLegacyMultiProduct(index) {
      const course = this.courses[index]
      if (!course) return false
      return course.products.length > 1 && !this.convertedProducts[index]
    },
    // Unlocks the picker for one legacy course. Nothing is written here — the admin
    // still has to pick a product and press Change — but it makes the reduction a
    // declared intent rather than a side effect of some unrelated edit on the tab.
    convertToSingleProduct(index) {
      this.$set(this.convertedProducts, index, true)
      // Start from no selection rather than an arbitrary "first" product. There is no
      // defensible rule for which of the products to keep (TWA-257), so the admin has
      // to choose; Change stays disabled until they do.
      this.$set(this.form.selected_products, index, null)
    },
    // TWA-257: the product_ids to send for this course, or null meaning "do not write
    // products at all". Null covers two cases: a locked legacy course, and a converted
    // one where the admin has not yet chosen the single product to keep.
    productIdsFor(index) {
      if (this.isLegacyMultiProduct(index)) return null
      const selected = this.form.selected_products[index]
      if (selected === null || selected === undefined || selected === '') return null
      return [selected]
    },
    is_button_disabled(index) {
      const course = this.courses[index]
      const form = this.form

      // Compare values between form and course
      const changes = {
        // TWA-257: selected_products is now a scalar id (single-select). A locked legacy
        // course contributes no product change at all, so its Change button stays driven
        // by the other fields — editing WTS there must not look like a product edit.
        products: this.productIdsFor(index) !== null &&
          JSON.stringify(this.productIdsFor(index)) !== JSON.stringify(course.products.map(p => p.id)),
        wts: form.allows_wts[index] !== course.allows_wts,
        teacherResources: form.shows_teacher_resources[index] !== course.shows_teacher_resources,
        autoEnroll: (form.auto_enroll_coupon_code[index] || '') !== (course.auto_enroll_coupon_code || ''),
        payment: form.accepts_payment[index] !== course.lti_consumer.accepts_payment,
        accessCode: form.accepts_access_code[index] !== course.lti_consumer.accepts_access_code,
        expiry: form.expires_at[index] !== course.expires_at
      }

      // Return true (disabled) if no changes detected
      return !Object.values(changes).some(changed => changed)
    },
    join_url(index) {
      let schema = 'https://'
      if (location.host === 'localhost:8080') {
        schema = 'http://'
      }
      return schema + location.host + '/verify?course=' + this.courses[index].context_id
    },
    async setProducts(index) {
      const current = this.courses[index]
      console.log('Updating', current.context_id, 'with products', JSON.stringify(this.productIdsFor(index)))
      // Build the request from a COPY so a failed save (e.g. a 422 on an unknown
      // auto-enroll code) never mutates the displayed row — otherwise the rejected
      // code sticks on the course object (lying "Bound:" readout, disabled retry
      // button). Also avoids mutating the shared lti_consumer object in place.
      const payload = {
        ...current,
        lti_consumer: { ...current.lti_consumer },
        // TWA-257: omitted entirely for a locked legacy course (productIdsFor returns
        // null) so an unrelated edit cannot reduce it. The backend leaves product_ids
        // optional, so an absent key means "leave the products alone".
        ...(this.productIdsFor(index) === null ? {} : { product_ids: this.productIdsFor(index) }),
        allows_wts: this.form.allows_wts[index],
        shows_teacher_resources: this.form.shows_teacher_resources[index],
        auto_enroll_coupon_code: this.form.auto_enroll_coupon_code[index],
        expires_at: this.form.expires_at[index],
      }
      payload.lti_consumer.accepts_payment = this.form.accepts_payment[index]
      payload.lti_consumer.accepts_access_code = this.form.accepts_access_code[index]
      try {
        this.$set(this.courses, index, await activateLtiCourse(payload))
      } catch (error) {
        // 422 = unknown auto-enroll code (or other validation) — surface the specific
        // reason; the displayed row is untouched, so the admin can fix the code and retry.
        this.notifyMe(getDetailedErrorMessage(error))
        return
      }
      const saved = this.courses[index].products
      // TWA-257: the row still carries several products, so the conversion did not
      // happen — re-lock it. The unlock is a declared intent to reduce; once a save has
      // come and gone without reducing, the next press has to declare it again.
      if (saved.length > 1) this.$delete(this.convertedProducts, index)
      // Scalar for the single-select, and only ever from a row that actually holds one
      // product. Preselecting one of four is the claim that makes Change look like a
      // real product edit; blank is the honest value.
      this.$set(this.form.selected_products, index, saved.length === 1 ? saved[0].id : null)
      this.$set(this.form.allows_wts, index, this.courses[index].allows_wts)
      this.$set(this.form.shows_teacher_resources, index, this.courses[index].shows_teacher_resources)
      this.$set(this.form.expires_at, index, this.courses[index].expires_at)
      this.$set(this.form.auto_enroll_coupon_code, index, this.courses[index].auto_enroll_coupon_code || '')
      this.$set(this.activations, index, this.courses[index].lti_activations);
      this.generateActivationHistory(index);
    },
    async checkCourse() {
      // TWA-215: the Submit button is disabled while a move is in flight, but the
      // Identifier field's Enter key reaches here too — and a search landing mid-move
      // replaces `courses`, so the confirm would swap the moved row into whatever sits
      // at that index in the new list. Same guard, on the method, so both doors close.
      if (this.isReassigning) return
      this.isSending = true
      // Defensive: if mounted-time getData() hasn't completed yet (race with a
      // fast click), make sure products + users are loaded before we render
      // the course tab — otherwise the products dropdown is empty.
      if (this.allProducts.length === 0) {
        await this.getData()
      }
      this.courses = []
      this.course_errors = []
      this.form.selected_products = []
      // TWA-257: the conversion unlock is per-lookup and index-keyed. Leaving it set
      // carries "already converted" onto whatever course lands at the same tab index
      // next — including a re-submit of the same identifier — unlocking a legacy row
      // nobody converted.
      this.convertedProducts = {}
      this.form.allows_wts = []
      this.form.shows_teacher_resources = []
      this.form.auto_enroll_coupon_code = []
      this.form.accepts_payment = []
      this.form.accepts_access_code = []
      this.form.expires_at = []
      this.form.reassign_key = []
      this.moveNotices = {}
      this.activations = []
      this.activation_histories = {}
      try {
        this.courses = await checkLtiCourses(this.form.context_id)
        if (this.courses && this.courses.length > 0) {
          this.courses.forEach((course, index) => {
            if (!this.form.selected_products[index]) {
              // TWA-257: scalar for the single-select, and only from a row that holds one
              // product; legacy rows render read-only text from course.products instead.
              this.$set(this.form.selected_products, index,
                course.products.length === 1 ? course.products[0].id : null);
              this.$set(this.form.allows_wts, index, course.allows_wts)
              this.$set(this.form.shows_teacher_resources, index, course.shows_teacher_resources)
              this.$set(this.form.auto_enroll_coupon_code, index, course.auto_enroll_coupon_code || '')
              this.$set(this.form.accepts_payment, index, course.lti_consumer.accepts_payment)
              this.$set(this.form.accepts_access_code, index, course.lti_consumer.accepts_access_code)
              this.$set(this.form.expires_at, index, course.expires_at)
              this.$set(this.form.reassign_key, index, '')
              this.$set(this.form.term_start_date, index, course.term_info?.start_at)
              this.$set(this.form.term_end_date, index, course.term_info?.end_at)
            }
            if (!this.activations[index]) {
              this.$set(this.activations, index, course.lti_activations);
            }
            this.generateActivationHistory(index);
          });
        }
        else {
          this.form.selected_products = []
        }
      } catch (error) {
        if (error.response && error.response.status === 404) {
          this.course_errors.push("Course not found");
        } else if (error.response && error.response.status === 500) {
          this.course_errors.push("Internal Server Error");
        } else {
          console.log(error)
        }
      }
      this.isSending = false
    },
    generateActivationHistory(index) {
      this.$set(this.activation_histories, index, []);
      this.activations[index].forEach(element => {
        let product_codes = [];
        if (element.product_ids && Array.isArray(element.product_ids)) {
          product_codes = element.product_ids.map(productId => {
            const product = this.products.find(p => p.id === productId);
            return product ? product.product_code : null;
          }).filter(Boolean);
        }

        this.activation_histories[index].push({
          date: new Date(element.activated_at).toLocaleString(),
          products: product_codes.join(','),
          user: element.actor_label
        });
      });
    },
    async clearExpiryDate(index) {
      this.$set(this.form.expires_at, index, null)
    },
    settingLabel(value, format) {
      if (value === null || value === undefined || value === '') return 'None'
      if (format === 'product') return this.productLabel(value)
      if (value === true) return 'Enabled'
      // ⚠️ NULL renders as 'None' and false as 'Disabled', so a NULL-vs-false pair WOULD
      // be listed as a change ("None → Disabled") — an earlier comment here claimed the
      // opposite. Nothing produces that pair today: allows_wts and shows_teacher_resources
      // are NOT NULL on lti_consumers, so the resolved inherited values are never NULL, and
      // no consumer has a NULL accepts_payment or accepts_access_code. A future NULL there
      // would show a spurious change, never hide a real one — which is the safe direction.
      if (value === false) return 'Disabled'
      return value
    },
    // Dry run first, then confirm, then move. The impact summary is computed server-side
    // so the admin confirms against real counts, not against what the row happens to show.
    async startReassign(index) {
      // The mirror of the guard in checkCourse: a search already in flight replaces
      // `courses` (and resets reassign_key) when it lands, so a move started now would
      // confirm against whatever row ends up at this index. Move is disabled on
      // isSending too; this closes the same door for a press that raced the flag.
      if (this.isSending) return
      const course = this.courses[index]
      const targetKey = this.form.reassign_key[index]
      this.isReassigning = true
      try {
        const { impact } = await reassignCourse(course.id, targetKey, true)
        // The dry run already knows the move will be refused. Say so now instead of
        // walking the admin through a full impact modal and a red confirm button for
        // a move the server will 422.
        if (impact.conflict) {
          this.notifyMe(`${targetKey} already has a row for this course (LtiCourse ${impact.conflict}). ` +
            `These two rows have to be merged, not moved.`)
          this.isReassigning = false
          return
        }
        if (impact.from.consumer_id === impact.to.consumer_id) {
          this.notifyMe(`This course is already under ${targetKey}.`)
          this.isReassigning = false
          return
        }
        this.reassignImpact = impact
        this.reassignIndex = index
      } catch (error) {
        this.notifyMe(getDetailedErrorMessage(error))
        this.isReassigning = false
        return
      }
      this.isReassigning = false

      const confirmed = await this.confirmReassign()
      if (!confirmed) return

      // ⚠️ Cleared HERE, not at Move-press time. A notice describes a move that already
      // happened, and the tab below it still shows the OLD school — so the likeliest next
      // action is the admin retyping that school and pressing Move again, which the dry
      // run refuses as same-consumer. Clearing on press wiped the warning on exactly that
      // refusal, leaving a stale tab with nothing saying so. Only a move that is actually
      // about to be attempted supersedes the last one's notice.
      this.$delete(this.moveNotices, index)
      this.isReassigning = true
      try {
        const { lti_course, message, duplicate_id } = await reassignCourse(course.id, targetKey, false)
        // TWA-829: the server re-checks after the move and tells us if a launch raced it
        // into creating a duplicate row. Nothing to undo — this is here so it is seen now
        // rather than months later as split enrollments.
        const dupeNotice = duplicate_id
          ? ` A duplicate row (LtiCourse ${duplicate_id}) appeared under ${targetKey} during the move —` +
            ' a launch raced it. These two rows need merging; report this.'
          : ''
        // The move can succeed while serializing it fails (Canvas down mid-request);
        // the server then confirms the move without the row. Report it and leave the
        // stale tab rather than blanking it — a re-search picks up the new state.
        if (!lti_course) {
          const text = (message || `Course moved to ${targetKey}. Re-search to refresh.`) + dupeNotice
          this.notifyMe(text, 'warning')
          // ...and leave it on the tab, because the row below still shows the OLD school
          // and the toast is gone in 2.5s. Without this an admin who looked away reads a
          // stale tab as current and can press Change against the previous consumer.
          this.$set(this.moveNotices, index, text)
          this.$set(this.form.reassign_key, index, '')
          this.isReassigning = false
          return
        }
        if (dupeNotice) {
          this.notifyMe(dupeNotice.trim(), 'warning')
          this.$set(this.moveNotices, index, dupeNotice.trim())
        }
        // Swap the row in place so the tab reflects its new school without a re-search.
        this.$set(this.courses, index, lti_course)
        this.$set(this.form.reassign_key, index, '')
        this.$set(this.form.allows_wts, index, lti_course.allows_wts)
        this.$set(this.form.shows_teacher_resources, index, lti_course.shows_teacher_resources)
        this.$set(this.form.auto_enroll_coupon_code, index, lti_course.auto_enroll_coupon_code || '')
        this.$set(this.form.accepts_payment, index, lti_course.lti_consumer.accepts_payment)
        this.$set(this.form.accepts_access_code, index, lti_course.lti_consumer.accepts_access_code)
        this.$set(this.activations, index, lti_course.lti_activations)
        this.generateActivationHistory(index)
        this.notifyMe(`Course moved to ${lti_course.lti_consumer.key}`, 'success')
      } catch (error) {
        // 422 = a row already exists under the target, so this needs a merge, not a
        // re-point. The message names the conflicting row.
        this.notifyMe(getDetailedErrorMessage(error))
      }
      this.isReassigning = false
    },
    confirmReassign() {
      return new Promise(resolve => {
        this.reassignResolver = resolve
        this.showReassignModal = true
      })
    },
    onReassignConfirm() {
      this.showReassignModal = false
      if (this.reassignResolver) {
        this.reassignResolver(true)
        this.reassignResolver = null
      }
    },
    onReassignCancel() {
      this.showReassignModal = false
      if (this.reassignResolver) {
        this.reassignResolver(false)
        this.reassignResolver = null
      }
    },
  },
  mounted() {
    this.getData()
  }
}
</script>
